/**
 * `IssueTrackerCard` — the workspace's inbound issue-tracker connection
 * (Jira today) in Workspace Settings → Integrations (#1558 Slice 1a
 * #1559). Mirrors screen A1 of docs/mockups/1558-issue-tracker-
 * integration.html.
 *
 * Self-contained: it loads its own connection list + provider descriptors
 * and renders one of two states, matching the GitHub/Slack cards on this
 * page:
 *   - **Connected** → a `<ProviderBlock>` with the base URL, last-checked
 *     time, and a kebab (Test connection / Update credentials / Disconnect).
 *   - **Not connected** → an inline connect form whose fields come from the
 *     provider descriptor's `credentialSchema` (no hard-coded Jira fields),
 *     with a single Connect action that validates-then-saves and shows the
 *     typed stage-1 result on failure.
 *
 * Stage-1 only: connecting proves the base URL is reachable and the
 * credentials authenticate — it does NOT check any project (that is the
 * app binding's stage-2, Slice 1b). So the connect result is one of the
 * three typed reasons, never the project/issue-type checklist the mockup
 * shows for the later slice.
 *
 * Admin-only in practice (the routes gate on `issueTrackers.write`); a
 * non-admin simply gets a 403 from the mutating calls, surfaced as an
 * inline error.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { KeyRound, RefreshCw, Trash2, CheckCircle2, AlertCircle } from 'lucide-react'
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { ProviderBlock, ConnectedBadge } from '@/components/ProviderBlock'
import {
  issueTrackersAPI,
  type IssueTrackerConnection,
  type IssueTrackerProviderDescriptor,
  type IssueTrackerCredentialField,
  type IssueTrackerValidationResult,
} from '@/lib/api'
import { fmtDate } from './ciIntegrationsUtils'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { ProviderCardShell } from './ProviderCardShell'

// ── Provider chrome ────────────────────────────────────────────────────────

const PROVIDER_NAMES: Record<string, string> = { jira: 'Jira' }

/** The Jira logo mark — the three cascading Atlassian-blue chevrons. Rendered
 * as an inline SVG (the same hand-authored approach as Slack's `SlackLogoBlock`
 * on this page) so it needs no external asset. `mono` fills every path with
 * `currentColor` for on-a-coloured-button use, where the gradient would have no
 * contrast (matches GitHub's white glyph on its dark button). */
function JiraLogo({ className, mono = false }: { className?: string; mono?: boolean }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      {!mono && (
        <defs>
          <linearGradient id="jira-mark-grad" x1="1" y1="0" x2="0.3" y2="1">
            <stop offset="0" stopColor="#2684FF" />
            <stop offset="1" stopColor="#0052CC" />
          </linearGradient>
        </defs>
      )}
      <path fill={mono ? 'currentColor' : '#0052CC'} d="M30.4 0H15.9c0 3.6 2.9 6.6 6.6 6.6h2.7v2.6c0 3.6 2.9 6.6 6.6 6.6V1.1C31.8.5 31.2 0 30.4 0Z" />
      <path fill={mono ? 'currentColor' : 'url(#jira-mark-grad)'} d="M23.2 7.3H8.7c0 3.6 2.9 6.6 6.6 6.6H18v2.6c0 3.6 3 6.6 6.6 6.6V8.3c0-.6-.6-1-1.4-1Z" />
      <path fill={mono ? 'currentColor' : 'url(#jira-mark-grad)'} d="M15.9 14.5H1.4c0 3.7 3 6.6 6.6 6.6h2.7v2.6c0 3.6 3 6.6 6.6 6.6V15.6c0-.6-.6-1.1-1.4-1.1Z" />
    </svg>
  )
}

/** Provider mark for the connected block / empty CTA. Jira gets its real logo;
 * an unknown future provider falls back to a neutral letter tile so a new
 * provider descriptor never renders blank before its mark is added. */
function ProviderMark({ provider, className }: { provider: string; className?: string }) {
  if (provider === 'jira') return <JiraLogo className={className} />
  return (
    <div
      className={`rounded-lg bg-muted-foreground/70 flex items-center justify-center text-white font-extrabold text-sm ${className ?? ''}`}
      aria-hidden="true"
    >
      {provider.slice(0, 1).toUpperCase()}
    </div>
  )
}

function providerLabel(provider: string): string {
  return PROVIDER_NAMES[provider] ?? provider
}

function providerConfigValue(connection: IssueTrackerConnection, key: string): string {
  const v = connection.providerConfig?.[key]
  return typeof v === 'string' ? v : ''
}

// ── Card ────────────────────────────────────────────────────────────────────

export function IssueTrackerCard() {
  const [connections, setConnections] = useState<IssueTrackerConnection[] | null>(null)
  const [providers, setProviders] = useState<IssueTrackerProviderDescriptor[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  // The connect-credentials dialog, opened from the empty-state CTA.
  const [connectOpen, setConnectOpen] = useState(false)
  // Controlled expand state for the collapsible shell. Null until the
  // connection state first resolves, then defaults to expanded when NOT
  // connected (surface the connect CTA) and collapsed when connected (the
  // details are reference) — matching the GitHub/Slack cards above. Once the
  // operator toggles, their choice wins.
  const [openState, setOpenState] = useState<boolean | null>(null)

  const reload = useCallback(async () => {
    try {
      const [conns, provs] = await Promise.all([
        issueTrackersAPI.list(),
        issueTrackersAPI.listProviders(),
      ])
      setConnections(conns)
      setProviders(provs)
      setLoadError(null)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load issue trackers')
    }
  }, [])

  useEffect(() => { void reload() }, [reload])

  // One inbound connection per workspace in Slice 1a (Jira only); take the
  // first. When a second provider lands this becomes a per-provider map.
  const connection = connections?.[0] ?? null
  const descriptor = useMemo(() => {
    if (!providers || providers.length === 0) return null
    if (connection) return providers.find((p) => p.provider === connection.provider) ?? providers[0]!
    return providers[0]!
  }, [providers, connection])

  const provider = connection?.provider ?? descriptor?.provider ?? 'jira'
  const connected = Boolean(connection && descriptor)

  // Initialise the expand state ONCE, when the connection first resolves —
  // expanded while not connected (the CTA is the call to action), collapsed
  // once connected (the details are reference). Matches the GitHub/Slack
  // heuristic; after this the operator's toggle wins, so connecting or
  // disconnecting mid-session doesn't snap the card they're looking at.
  useEffect(() => {
    if (openState !== null || connections === null) return
    setOpenState(!connected)
  }, [connections, connected, openState])

  const open = openState ?? !connected
  // Collapsed-header summary mirrors the Slack card's "Connected · <team>":
  // show the host (base URL without the scheme) so the operator sees which
  // site is wired up without expanding.
  const host = connection ? providerConfigValue(connection, 'baseUrl').replace(/^https?:\/\//, '') : ''
  const summary = connected ? (
    <span className="text-emerald-700 dark:text-emerald-300">Connected{host ? ` · ${host}` : ''}</span>
  ) : (
    <span className="text-muted-foreground">Not connected</span>
  )

  // Before the first fetch resolves, show a bare skeleton — we don't yet know
  // the connection state needed to title/summarize the shell. Mirrors the
  // GitHub/Slack cards' loading skeleton.
  if (connections === null && !loadError) {
    return <div className="rounded-lg border border-border h-16 animate-pulse bg-muted/30" aria-hidden="true" />
  }

  return (
    <ProviderCardShell
      headingId="issue-tracker-card"
      providerName={providerLabel(provider)}
      summary={summary}
      open={open}
      onOpenChange={setOpenState}
    >
      {loadError && <p className="text-sm text-destructive" role="alert">{loadError}</p>}
      {actionError && <p className="text-sm text-destructive" role="alert">{actionError}</p>}

      {connected ? (
        <ConnectedTrackerBlock
          connection={connection!}
          descriptor={descriptor!}
          onChanged={() => void reload()}
          onError={setActionError}
        />
      ) : descriptor ? (
        <>
          <JiraEmptyCard
            provider={provider}
            onConnect={() => { setActionError(null); setConnectOpen(true) }}
          />
          <CredentialsDialog
            open={connectOpen}
            mode="connect"
            descriptor={descriptor}
            onClose={() => setConnectOpen(false)}
            onSaved={() => { setConnectOpen(false); setActionError(null); void reload() }}
            onError={setActionError}
          />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">No issue-tracker providers are available.</p>
      )}
    </ProviderCardShell>
  )
}

// ── Not-connected: the centered CTA ──────────────────────────────────────────

/** Empty state — a centered call-to-action matching the GitHub/Slack empty
 * cards on this page (logo + one line + a single Connect button). Unlike those
 * two (which redirect to OAuth), Jira needs a base URL + token, so the button
 * opens the credentials dialog rather than navigating away. */
function JiraEmptyCard({ provider, onConnect }: { provider: string; onConnect: () => void }) {
  const label = providerLabel(provider)
  return (
    <div className="text-center py-2">
      <ProviderMark provider={provider} className="mx-auto mb-3 h-10 w-10" />
      <h3 className="text-sm font-semibold">Connect {label} to pull issues into coverage</h3>
      <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
        Store your {label} credentials once here at the workspace. Each app then picks which
        projects to pull from — no per-app token.
      </p>
      <button
        type="button"
        onClick={onConnect}
        className="mt-3 inline-flex items-center gap-2 rounded-md bg-[#0052cc] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0747a6] transition-colors"
      >
        <JiraLogo className="h-3.5 w-3.5" mono />
        Connect {label}
      </button>
    </div>
  )
}

// ── Typed result banner ─────────────────────────────────────────────────────

/** Renders the typed stage-1 result. Success is transient (the caller
 * reloads into the connected state), so this only ever shows a failure. */
function ValidationResultBanner({ result }: { result: IssueTrackerValidationResult }) {
  if (result.ok) {
    return (
      <div className="flex items-center gap-2 text-sm rounded-lg border border-green-300 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-300 px-3 py-2">
        <CheckCircle2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span>Connected — the credentials work.</span>
      </div>
    )
  }
  return (
    <div
      role="alert"
      className="flex items-start gap-2 text-sm rounded-lg border border-destructive/50 bg-destructive/10 text-destructive px-3 py-2"
    >
      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <span>{result.message}</span>
    </div>
  )
}

// ── Credential fields (descriptor-driven) ───────────────────────────────────

/** Renders one input per credential-schema field. Secret fields use a
 * password input; `placeholders` overrides the placeholder per field (used
 * by the update dialog's "leave blank to keep" hint). */
function CredentialFields({
  fields,
  values,
  onChange,
  disabled,
  placeholders,
  idPrefix,
}: {
  fields: IssueTrackerCredentialField[]
  values: Record<string, string>
  onChange: (key: string, value: string) => void
  disabled: boolean
  placeholders?: Record<string, string>
  idPrefix: string
}) {
  return (
    <div className="space-y-3">
      {fields.map((field) => {
        const id = `${idPrefix}-${field.key}`
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={id}>{field.label}</Label>
            <Input
              id={id}
              type={field.secret ? 'password' : 'text'}
              autoComplete={field.secret ? 'new-password' : 'off'}
              value={values[field.key] ?? ''}
              placeholder={placeholders?.[field.key]}
              onChange={(e) => onChange(field.key, e.target.value)}
              disabled={disabled}
            />
          </div>
        )
      })}
    </div>
  )
}

// ── Connected: the provider block + actions ─────────────────────────────────

function ConnectedTrackerBlock({
  connection,
  descriptor,
  onChanged,
  onError,
}: {
  connection: IssueTrackerConnection
  descriptor: IssueTrackerProviderDescriptor
  onChanged: () => void
  onError: (msg: string) => void
}) {
  const tz = useUserTimezone()
  const [updateOpen, setUpdateOpen] = useState(false)
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<IssueTrackerValidationResult | null>(null)

  const baseUrl = providerConfigValue(connection, 'baseUrl')
  // A non-secret identity field to show under the base URL, if the
  // provider has one (Jira: email). First non-secret, non-baseUrl field.
  const identityField = descriptor.credentialSchema.find(
    (f) => !f.secret && f.key !== 'baseUrl',
  )
  const identityValue = identityField ? providerConfigValue(connection, identityField.key) : ''

  async function handleTest() {
    setTesting(true)
    setTestResult(null)
    try {
      setTestResult(await issueTrackersAPI.validate(connection.id))
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to check the connection')
    } finally {
      setTesting(false)
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true)
    try {
      await issueTrackersAPI.delete(connection.id)
      setDisconnectOpen(false)
      onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to disconnect the issue tracker')
    } finally {
      setDisconnecting(false)
    }
  }

  const menuItems = [
    {
      key: 'test',
      label: testing ? 'Checking…' : 'Test connection',
      icon: <RefreshCw className="h-3.5 w-3.5" />,
      disabled: testing,
      onClick: () => void handleTest(),
    },
    {
      key: 'update',
      label: 'Update credentials',
      icon: <KeyRound className="h-3.5 w-3.5" />,
      onClick: () => setUpdateOpen(true),
    },
    {
      key: 'disconnect',
      label: 'Disconnect',
      icon: <Trash2 className="h-3.5 w-3.5" />,
      danger: true,
      separator: true,
      onClick: () => setDisconnectOpen(true),
    },
  ]

  return (
    <>
      <ProviderBlock
        mark={<ProviderMark provider={connection.provider} className="h-8 w-8" />}
        name={providerLabel(connection.provider)}
        subtitle={baseUrl || 'connection'}
        statusBadge={<ConnectedBadge />}
        menuItems={menuItems}
      >
        <div className="text-xs text-muted-foreground space-y-0.5">
          {identityValue && <div>Signed in as {identityValue}</div>}
          <div>Last checked: {fmtDate(connection.lastValidatedAt, tz)}</div>
          <div>Connected: {fmtDate(connection.createdAt, tz)}</div>
        </div>
        {testResult && (
          <div className="mt-3">
            <ValidationResultBanner result={testResult} />
          </div>
        )}
      </ProviderBlock>

      <CredentialsDialog
        open={updateOpen}
        mode="update"
        connection={connection}
        descriptor={descriptor}
        onClose={() => setUpdateOpen(false)}
        onSaved={() => { setUpdateOpen(false); onChanged() }}
        onError={onError}
      />

      <ConfirmDialog
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        title={`Disconnect ${providerLabel(connection.provider)}?`}
        description={
          <>
            OttoTester will stop reading issues from{' '}
            <strong>{baseUrl || 'this tracker'}</strong>. The stored token is deleted. Apps that
            pull from it will show as disconnected until you connect again.
          </>
        }
        confirmLabel="Disconnect"
        confirmVariant="destructive"
        onConfirm={handleDisconnect}
        busy={disconnecting}
      />
    </>
  )
}

// ── Connect / update credentials dialog ─────────────────────────────────────

/**
 * One dialog for both connecting a new issue tracker and updating an existing
 * connection's credentials (#1558). The two flows share the descriptor-driven
 * fields, the validate-then-save contract, and the typed failure banner; only
 * the copy, the initial values, and the save call differ:
 *
 *   - **connect** — empty fields, all required, calls `connect`.
 *   - **update**  — non-secret fields pre-filled from the stored config; the
 *     secret starts blank with a "leave blank to keep" hint (we never
 *     round-trip the token) and a blank secret is omitted so the stored one is
 *     kept; calls `updateCredentials`.
 *
 * On success the caller reloads into the connected state; on a typed failure
 * the banner shows the reason and nothing is persisted.
 */
function CredentialsDialog({
  open,
  mode,
  descriptor,
  connection,
  onClose,
  onSaved,
  onError,
}: {
  open: boolean
  mode: 'connect' | 'update'
  descriptor: IssueTrackerProviderDescriptor
  /** Required for `update` (the connection being edited); absent for `connect`. */
  connection?: IssueTrackerConnection
  onClose: () => void
  onSaved: () => void
  onError: (msg: string) => void
}) {
  const isUpdate = mode === 'update'
  const label = providerLabel(descriptor.provider)

  // Update pre-fills non-secret fields from the stored config; connect starts
  // blank. Secret fields always start blank.
  const initialValues = useMemo(() => {
    const v: Record<string, string> = {}
    for (const f of descriptor.credentialSchema) {
      v[f.key] = isUpdate && !f.secret && connection ? providerConfigValue(connection, f.key) : ''
    }
    return v
    // Re-seed whenever the dialog (re)opens for this connection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, descriptor, isUpdate, open])

  const [values, setValues] = useState<Record<string, string>>(initialValues)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<IssueTrackerValidationResult | null>(null)

  useEffect(() => {
    if (open) {
      setValues(initialValues)
      setResult(null)
    }
  }, [open, initialValues])

  // Connect requires every field. Update requires the non-secret fields but
  // lets the secret stay blank (= keep the stored token).
  const allFilled = descriptor.credentialSchema.every((f) =>
    isUpdate && f.secret ? true : (values[f.key] ?? '').trim().length > 0,
  )

  const placeholders = isUpdate
    ? Object.fromEntries(
        descriptor.credentialSchema
          .filter((f) => f.secret)
          .map((f) => [f.key, 'Leave blank to keep the current value']),
      )
    : undefined

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!allFilled) return
    setSubmitting(true)
    setResult(null)
    try {
      // Trim every field. For update, omit a blank secret so the backend keeps
      // the stored token; every other field is sent as entered.
      const credentials: Record<string, string> = {}
      for (const f of descriptor.credentialSchema) {
        const val = (values[f.key] ?? '').trim()
        if (isUpdate && f.secret) {
          if (val.length > 0) credentials[f.key] = val
        } else {
          credentials[f.key] = val
        }
      }
      const outcome = isUpdate
        ? await issueTrackersAPI.updateCredentials(connection!.id, credentials)
        : await issueTrackersAPI.connect({ provider: descriptor.provider, credentials })
      if (outcome.ok) {
        onSaved()
      } else {
        setResult(outcome.validation)
      }
    } catch (err) {
      onError(
        err instanceof Error
          ? err.message
          : isUpdate
            ? 'Failed to update the connection'
            : 'Failed to connect the issue tracker',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isUpdate ? `Update ${label} credentials` : `Connect ${label}`}</DialogTitle>
          <DialogDescription>
            {isUpdate
              ? 'We check the new credentials before saving them. Leave the token blank to keep the current one.'
              : 'Store your credentials once here at the workspace. We check the connection before saving it.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)}>
          <DialogBody className="space-y-4">
            <CredentialFields
              fields={descriptor.credentialSchema}
              values={values}
              onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))}
              disabled={submitting}
              placeholders={placeholders}
              idPrefix={isUpdate ? 'it-update' : 'it-connect'}
            />
            {result && <ValidationResultBanner result={result} />}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !allFilled}>
              {submitting ? 'Checking…' : isUpdate ? 'Save changes' : 'Connect'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
